<template>
  <article class="avatar" :class="avatarClass" :style="avatarStyle">
    <span class="presence" v-if="presence" :class="presenceClass" :style="presenceStyle"/>
    <span class="initials" v-if="!hasImage && !identicon">{{ initials }}</span>
  </article>
</template>

<script>
import { getColourInvert } from '../../utilities/colour/get-colour-invert'
import md5 from 'js-md5'

export default {
  props: {
    path: String,
    name: {
      type: String,
      default: ''
    },
    size: {
      type: Number,
      default: 48
    },
    rounded: {
      type: Boolean,
      default: true
    },
    inline: {
      type: Boolean,
      default: false
    },
    empty: {
      type: String,
      default: '?'
    },
    identicon: {
      type: Boolean | String,
      default: false
    },
    presence: {
      type: String,
      default: '',
    },
    colorize: {
      type: Boolean,
      default: false,
    }
  },

  data: () => ({
    hasImage: false,
  }),

  created() {
    this.render()
  },

  watch: {
    path: 'render'
  },

  computed: {
    initials() {
      let initials = this.name.match(/\b(\w)/g)

      if (!initials) return this.empty
      initials = initials.length > 2
        ? [initials[0], initials[initials.length - 1]]
        : initials

      return initials.join('').toUpperCase()
    },

    defaultImage() {
      return this.identicon
        ? `url(https://www.gravatar.com/avatar/${md5(this.identicon)}?d=identicon&f=y)`
        : false
    },

    avatarClass() {
      return {
        'no-image': !this.hasImage && !this.identicon,
        'is-rounded': this.rounded
      }
    },

    avatarStyle() {
      return {
        display: this.inline ? 'inline-flex' : 'flex',
        width: `${this.size}px`,
        height: `${this.size}px`,
        backgroundImage: this.hasImage ? `url(${this.path})` : this.defaultImage,
        lineHeight: `${(this.size + Math.floor(this.size / 20))}px`,
        fontSize: `${Math.floor(this.size / 2.5)}px`,
        ...this.colorized,
      }
    },

    presenceClass() {
      if (!this.presence) return null

      const colorClass = {
        available: 'is-success',
        busy: 'is-danger',
        meeting: 'is-warning',
        tentative: 'is-info',
      }[this.presence]

      return colorClass || this.presence
    },

    presenceStyle() {
      let size = this.size * 25 / 100
      let rounded = 2 * Math.round(size / 2)

      return {
        height: `${rounded}px`,
        width: `${rounded}px`,
      }
    },

    colorized() {
      if (!this.colorize || !this.initials) return null

      const getHashCode = input => {
        let hash = 0
        if (input.length == 0) return hash
        for (let i = 0; i < input.length; i++) {
          hash = input.charCodeAt(i) + ((hash << 6) - hash)
          hash = hash & hash
        }
        return hash
      };

      const toHsl = input => {
        const shortened = input % 360
        return `hsl(${shortened}, 100%, 30%)`
      }

      const backgroundColor = toHsl(getHashCode(this.initials))

      return {
        backgroundColor,
        color: getColourInvert(backgroundColor)
      }
    }
  },

  methods: {
    render() {
      if (this.path) {
        let image = new Image
        image.onerror = () => this.hasImage = false
        image.onload = () => this.hasImage = true
        image.src = this.path
      }
    },
  }
}
</script>
